import apiClient from "@/api/client";
import type {
  AiSheet,
  AiSheetColumn,
  AiSheetData,
  AiSheetDimension,
  AiSheetFilters,
  AiSheetOptions,
  AiSheetRow,
  RowCandidate,
  RowsSyncResult,
  SpendInfo,
} from "./types";

const BASE = "/power-projects/ai-sheets";

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

export const listSheets = () => unwrap<AiSheet[]>(apiClient.get(`${BASE}/`));

export const createSheet = (v: {
  name: string;
  row_dimension: AiSheetDimension;
  filters: AiSheetFilters;
}) => unwrap<AiSheet>(apiClient.post(`${BASE}/`, v));

export const patchSheet = (id: string, v: Partial<Pick<AiSheet, "name" | "filters" | "transposed">>) =>
  unwrap<AiSheet>(apiClient.patch(`${BASE}/${id}/`, v));

export const deleteSheet = (id: string) => apiClient.delete(`${BASE}/${id}/`);

export const getOptions = () => unwrap<AiSheetOptions>(apiClient.get(`${BASE}/options/`));
export const getSpend = () => unwrap<SpendInfo>(apiClient.get(`${BASE}/spend/`));
export const setBudget = (budget_usd: string) =>
  unwrap<SpendInfo>(apiClient.post(`${BASE}/spend/`, { budget_usd }));
export const getData = (id: string) => unwrap<AiSheetData>(apiClient.get(`${BASE}/${id}/data/`));

export const rowsSync = (id: string, apply: boolean) =>
  unwrap<RowsSyncResult>(apiClient.post(`${BASE}/${id}/rows/sync/`, { apply }));
export const rowsAdd = (id: string, entity_id: string) =>
  unwrap<AiSheetRow>(apiClient.post(`${BASE}/${id}/rows/add/`, { entity_id }));
export const rowsRemove = (id: string, row_id: string) =>
  apiClient.post(`${BASE}/${id}/rows/remove/`, { row_id });
export const rowsCandidates = (id: string, search: string) =>
  unwrap<RowCandidate[]>(apiClient.get(`${BASE}/${id}/rows/candidates/`, { params: { search } }));

export const addSystemColumn = (id: string, field: string) =>
  unwrap<AiSheetColumn>(apiClient.post(`${BASE}/${id}/columns/system/`, { field }));
export const addManualColumn = (id: string, v: { label: string; fmt: string }) =>
  unwrap<AiSheetColumn>(apiClient.post(`${BASE}/${id}/columns/manual/`, v));
export const setCellValue = (
  id: string,
  v: { row_id: string; column_id: string; value: string | number | null },
) => unwrap<import("./types").AiSheetCell>(apiClient.post(`${BASE}/${id}/cells/set/`, v));

export const planColumn = (
  id: string,
  v: { label: string; description: string; replace_column_id?: string },
) => unwrap<{ task_id: string }>(apiClient.post(`${BASE}/${id}/columns/plan/`, v));
/** One turn of the column conversation. On an applied column the server opens a
 * draft that will replace it — hence column_id in the answer. */
export const chatColumn = (id: string, colId: string, message: string) =>
  unwrap<{ task_id: string; column_id: string }>(
    apiClient.post(`${BASE}/${id}/columns/${colId}/chat/`, { message }),
  );
export const applyColumn = (id: string, colId: string, force = false) =>
  unwrap<{ column: AiSheetColumn; task_id: string }>(
    apiClient.post(`${BASE}/${id}/columns/${colId}/apply/`, force ? { force: true } : {}),
  );
export const discardColumn = (id: string, colId: string) =>
  apiClient.post(`${BASE}/${id}/columns/${colId}/discard/`, {});
export const patchColumn = (id: string, colId: string, v: { label?: string; position?: number }) =>
  unwrap<AiSheetColumn>(apiClient.patch(`${BASE}/${id}/columns/${colId}/`, v));
export const deleteColumn = (id: string, colId: string) =>
  apiClient.delete(`${BASE}/${id}/columns/${colId}/`);

export const refreshSheet = (
  id: string,
  v: { scope: "all" | "row" | "column" | "cell"; row_id?: string; column_id?: string },
) => unwrap<{ task_id: string; cells: number }>(apiClient.post(`${BASE}/${id}/refresh/`, v));

export const shareEligible = (id: string) =>
  unwrap<import("./types").ShareCandidate[]>(apiClient.get(`${BASE}/${id}/share/eligible/`));
export const shareSheet = (id: string, userId: string) =>
  apiClient.post(`${BASE}/${id}/share/`, { user_id: userId });
export const unshareSheet = (id: string, userId: string) =>
  apiClient.post(`${BASE}/${id}/unshare/`, { user_id: userId });
export const copySheet = (id: string) =>
  unwrap<AiSheet>(apiClient.post(`${BASE}/${id}/copy/`, {}));
export const moveRow = (id: string, rowId: string, direction: "up" | "down") =>
  apiClient.post(`${BASE}/${id}/rows/move/`, { row_id: rowId, direction });
export const moveColumn = (id: string, columnId: string, direction: "left" | "right") =>
  apiClient.post(`${BASE}/${id}/columns/move/`, { column_id: columnId, direction });
